import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import {
    HoneypotField,
    useFormSecurity,
    withFormSecurity,
} from "../../lib/formSecurity";
import { SAAS_API_URL } from "../../lib/env";
import {
    CONTACT_REASONS,
    CONTACT_SEAT_OPTIONS,
    CONTACT_TIMELINE_OPTIONS,
    DEFAULT_CONTACT_SALES_FORM,
} from "../../data/contactSalesContent";

const SAAS_API = SAAS_API_URL;

export function ContactSalesForm({
    className = "",
    showHeader = true,
    showEmailFallback = true,
    defaultReason = DEFAULT_CONTACT_SALES_FORM.reason,
    defaultSeats = DEFAULT_CONTACT_SALES_FORM.seats,
    defaultTimeline = DEFAULT_CONTACT_SALES_FORM.timeline,
    onSuccess,
    onReasonChange,
    formId = "contact-sales-form",
}) {
    const [form, setForm] = useState({
        ...DEFAULT_CONTACT_SALES_FORM,
        reason: defaultReason,
        seats: defaultSeats,
        timeline: defaultTimeline,
    });
    const [submitting, setSubmitting] = useState(false);
    const { security, setSecurity, resetSecurity } = useFormSecurity();

    useEffect(() => {
        setForm((prev) => ({ ...prev, reason: defaultReason }));
    }, [defaultReason]);

    const activeReason =
        CONTACT_REASONS.find((r) => r.id === form.reason) ?? CONTACT_REASONS[0];

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.company) {
            toast.error("Please fill name, work email and company.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("That email doesn’t look right.");
            return;
        }

        setSubmitting(true);
        try {
            const res = await fetch(`${SAAS_API}/api/public/v1/contact-inquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(
                    withFormSecurity(
                        {
                            source: "contact_sales",
                            name: form.name,
                            email: form.email,
                            company: form.company,
                            website_url: form.website_url.trim() || null,
                            seats: form.seats,
                            timeline: form.timeline,
                            message: form.message,
                            topic: form.reason,
                            topic_label: activeReason.label,
                        },
                        security,
                    ),
                ),
            });
            const json = await res.json();
            if (!res.ok || !json.success) {
                throw new Error(json.message || "Submission failed");
            }
            toast.success(
                "Got it — a sales engineer will reach out within one business day.",
            );
            setForm({
                ...DEFAULT_CONTACT_SALES_FORM,
                reason: defaultReason,
                seats: defaultSeats,
                timeline: defaultTimeline,
            });
            resetSecurity();
            onSuccess?.();
        } catch (err) {
            toast.error(
                err.message ||
                    "Could not send your message. Try sales@apexbrain.in",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            id={formId}
            onSubmit={submit}
            className={className}
            data-testid="contact-sales-form"
        >
            <HoneypotField
                value={security._hp}
                onChange={(v) => setSecurity((s) => ({ ...s, _hp: v }))}
            />
            {showHeader && (
                <>
                    <p className="relative font-code text-[10px] uppercase tracking-[0.2em] text-electric mb-2">
                        // send the brief
                    </p>
                    <h2 className="relative font-outfit text-2xl font-semibold text-white">
                        Tell us about your team.
                    </h2>
                    <p className="relative text-xs text-zinc-500 mt-1">
                        A sales engineer reads every submission within one
                        business day.
                    </p>
                </>
            )}

            <div className="relative mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                    label="Your name"
                    testId="contact-form-name"
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    placeholder="Aanya Mehta"
                />
                <Field
                    label="Work email"
                    testId="contact-form-email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    placeholder="aanya@yourcompany.com"
                />
                <Field
                    label="Company"
                    testId="contact-form-company"
                    value={form.company}
                    onChange={(v) => setForm({ ...form, company: v })}
                    placeholder="Ledgerly"
                />
                <Field
                    label="Website"
                    testId="contact-form-website"
                    value={form.website_url}
                    onChange={(v) => setForm({ ...form, website_url: v })}
                    placeholder="https://yourcompany.com"
                />
                <Select
                    label="Reason"
                    testId="contact-form-reason"
                    value={form.reason}
                    onChange={(v) => {
                        setForm({ ...form, reason: v });
                        onReasonChange?.(v);
                    }}
                    options={CONTACT_REASONS.map((r) => ({
                        value: r.id,
                        label: r.label,
                    }))}
                />
                <Select
                    label="Seats expected"
                    testId="contact-form-seats"
                    value={form.seats}
                    onChange={(v) => setForm({ ...form, seats: v })}
                    options={CONTACT_SEAT_OPTIONS}
                />
                <Select
                    label="Timeline"
                    testId="contact-form-timeline"
                    value={form.timeline}
                    onChange={(v) => setForm({ ...form, timeline: v })}
                    options={CONTACT_TIMELINE_OPTIONS}
                />
            </div>
            <div className="relative mt-4">
                <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
                    What are you trying to do?
                </label>
                <textarea
                    data-testid="contact-form-message"
                    value={form.message}
                    onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                    }
                    rows={4}
                    placeholder="One paragraph is plenty — we'll come back with questions."
                    className="w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 placeholder:text-zinc-600"
                />
            </div>
            <button
                type="submit"
                disabled={submitting}
                data-testid="contact-form-submit"
                className="relative mt-5 group inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-6 py-3 rounded-full hover:brightness-110 transition disabled:opacity-60"
            >
                {submitting ? "Sending…" : "Talk to sales"}
                <Send className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </button>
            {showEmailFallback && (
                <p className="relative text-[11px] text-zinc-600 font-code mt-3">
                    Or email{" "}
                    <a
                        className="text-zinc-300 hover:text-white"
                        href="mailto:sales@apexbrain.in"
                    >
                        sales@apexbrain.in
                    </a>{" "}
                    directly.
                </p>
            )}
        </form>
    );
}

const Field = ({ label, value, onChange, placeholder, type = "text", testId }) => (
    <div>
        <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
            {label}
        </label>
        <input
            type={type}
            data-testid={testId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 placeholder:text-zinc-600"
        />
    </div>
);

const Select = ({ label, value, onChange, options, testId, disabled }) => (
    <div>
        <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
            {label}
        </label>
        <select
            data-testid={testId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={`w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 font-code ${disabled ? "opacity-70" : ""}`}
        >
            {options.map((o) => {
                const optionValue = typeof o === "object" ? o.value : o;
                const optionLabel = typeof o === "object" ? o.label : o;
                return (
                    <option
                        key={optionValue}
                        value={optionValue}
                        className="bg-obsidian-300"
                    >
                        {optionLabel}
                    </option>
                );
            })}
        </select>
    </div>
);
